import type {
	OptimizationModel,
	PricingCampaignDuplicateRequest,
} from '@/shared/generated'

import { Fragment, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { SelectOptimizationGroups } from '@/features/select-optimization-groups'

import { resetOptimizations } from '@/entities/optimization'
import { useDublicatePricingCampaignsMutation } from '@/entities/pricing-campaign'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import useStep from '@/shared/hooks/useStep'

import Checkout from './CheckoutView'
import ExecutionView from './ExecutionView'

type Props = {
	isOpen: boolean
	close(): void
	selected: string[]
	resetSelectedItems?(): void
}
export const DuplicatePricingCampaignsModal = ({
	isOpen,
	close,
	selected: selectedCampaigns,
	resetSelectedItems,
}: Props) => {
	const { t } = useTranslation()

	const [selectedGroups, setSelectedGroups] = useState<string[]>([])
	const [currentStep, helpers] = useStep({ maxStep: 3 })
	const { goToNextStep, goToPrevStep } = helpers
	const queryClient = useQueryClient()
	const dublicate = useDublicatePricingCampaignsMutation({
		mutationKey: ['pricing-campaign-duplicate'],
		async onMutate(variables: PricingCampaignDuplicateRequest) {
			const optimizations = queryClient.getQueryData<OptimizationModel[]>([
				'optimizations',
			])
			await resetOptimizations(
				optimizations || [],
				variables.dst_entity_ids as string[]
			)
		},
		onSuccess() {
			resetSelectedItems?.()
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-segments'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricings-settings'],
			})
			queryClient.invalidateQueries({
				queryKey: ['products-counts-by-pc'],
			})
		},
	})
	const formData = {
		campaigns: selectedCampaigns,
		groups: selectedGroups,
	}
	const handleExecute = () => {
		const requestBody: PricingCampaignDuplicateRequest = {
			src_pricing_campaign_ids: formData.campaigns,
			dst_entity_type: 'optimization_group',
			dst_entity_ids: formData.groups,
		}
		dublicate.mutateAsync(requestBody)
	}

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='max-w-5xl'>
				{currentStep === 1 && (
					<Fragment>
						<ModalHeader>
							<ModalTitle>{t('pc.modal.dublicate_1.title')}</ModalTitle>
							<ModalDescription>
								{t('pc.modal.dublicate_1.subtitle')}
							</ModalDescription>
						</ModalHeader>
						<ErrorBoundary>
							<ModalBody className='space-y-3'>
								<SelectOptimizationGroups
									selected={selectedGroups}
									setSelected={setSelectedGroups}
								/>
								<Alert variant='info'>
									<InfoIcon />
									<AlertDescription>
										{t('note.copy_pc_general_note')}
									</AlertDescription>
								</Alert>
							</ModalBody>
						</ErrorBoundary>
						<ModalFooter>
							<ModalClose asChild>
								<Button className='mr-auto'>{t('general_cancel')}</Button>
							</ModalClose>
							<Button
								variant='primary'
								disabled={selectedGroups.length === 0}
								onClick={goToNextStep}
							>
								{t('general_next')}
								<ChevronRightIcon />
							</Button>
						</ModalFooter>
					</Fragment>
				)}
				{currentStep === 2 && (
					<Fragment>
						<ModalHeader>
							<ModalTitle>{t('pc.modal.dublicate_2.title')}</ModalTitle>
							<ModalDescription>
								{t('pc.modal.dublicate_2.subtitle')}
							</ModalDescription>
						</ModalHeader>
						<ErrorBoundary>
							<ModalBody className='space-y-3'>
								<Checkout formData={formData} />
								<Alert variant='info'>
									<InfoIcon />
									<AlertDescription>
										{t('note.copy_pc_general_note')}
									</AlertDescription>
								</Alert>
							</ModalBody>
						</ErrorBoundary>
						<ModalFooter>
							<ModalClose asChild>
								<Button className='mr-auto'>{t('general_cancel')}</Button>
							</ModalClose>
							<Button variant='secondary' onClick={goToPrevStep}>
								<ChevronLeftIcon />
								{t('general_back')}
							</Button>
							<Button
								variant='primary'
								onClick={() => {
									handleExecute()
									goToNextStep()
								}}
							>
								{t('app.duplicate_pcs')}
								<ChevronRightIcon />
							</Button>
						</ModalFooter>
					</Fragment>
				)}
				{currentStep === 3 && (
					<Fragment>
						<ModalHeader>
							<ModalTitle>{t('pc.modal.dublicate_3.title')}</ModalTitle>
							<ModalDescription>
								{t('pc.modal.dublicate_3.subtitle')}
							</ModalDescription>
						</ModalHeader>
						<ErrorBoundary>
							<ModalBody className='min-h-[400px]'>
								<ExecutionView formData={formData} run={dublicate} />
							</ModalBody>
						</ErrorBoundary>
						<ModalFooter>
							<Button
								disabled={dublicate.isPending}
								variant='secondary'
								onClick={goToPrevStep}
							>
								<ChevronLeftIcon />
								{t('general_back')}
							</Button>
							<ModalClose asChild>
								<Button disabled={dublicate.isPending} variant='primary'>
									{t('general_close_and_finish')}
								</Button>
							</ModalClose>
						</ModalFooter>
					</Fragment>
				)}
			</ModalContent>
		</Modal>
	)
}
